import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Input, RadioButton, TextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function COTFCPlacementSheet() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const yesNoOptions = [
    {
      _id: "1",
      name: "Yes",
    },
    { _id: "0", name: "No" },
  ];

  const permanencyPlanOptions = [
    {
      _id: "1",
      name: "Reunification",
    },
    {
      _id: "2",
      name: "Adoption",
    },
    { _id: "3", name: "Kinship" },
    { _id: "4", name: "Other" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <h4 className="text-center text-safy1-1">
                SAFY Treatment Foster Care Placement Sheet #F-502
              </h4>
              <h6 className="text-center mt-4">Basic Information</h6>
              <TextArea
                name="YouthStrengths"
                label="Youth Strengths:"
                error={errors.YouthStrengths}
                value={data.YouthStrengths["@value"]}
                onChange={handleChange}
                identical={compare("YouthStrengths")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="YouthPlaceOfBirth"
                label="Youth's Place of Birth"
                error={errors.YouthPlaceOfBirth}
                value={data.YouthPlaceOfBirth["@value"]}
                onChange={handleChange}
                identical={compare("YouthPlaceOfBirth")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <h6 className="text-center mt-4">Placement Needs</h6>
              <TextArea
                name="ReligiousNeeds"
                label="Religious Preferences or Needs:"
                error={errors.ReligiousNeeds}
                value={data.ReligiousNeeds["@value"]}
                onChange={handleChange}
                identical={compare("ReligiousNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SpecialNeeds"
                label="Special Needs:"
                error={errors.SpecialNeeds}
                value={data.SpecialNeeds["@value"]}
                onChange={handleChange}
                identical={compare("SpecialNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="PermanencyPlan"
                label="Permanency Plan:"
                options={permanencyPlanOptions}
                error={errors.PermanencyPlan}
                value={data.PermanencyPlan["@value"]}
                onChange={handleChange}
                identical={compare("PermanencyPlan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.PermanencyPlan["@value"] === "4" && (
                <TextArea
                  name="PermanencyPlanOther"
                  error={errors.PermanencyPlanOther}
                  value={data.PermanencyPlanOther["@value"]}
                  onChange={handleChange}
                  identical={compare("TextAre")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <h6 className="text-center mt-4">
                Physical and Behavioral Health
              </h6>
              <RadioButton
                name="FirstEntry"
                label="Is this the client’s 1st entry into care or are they re-entering care from home/kinship care/adoption?"
                options={yesNoOptions}
                error={errors.FirstEntry}
                value={data.FirstEntry["@value"]}
                onChange={handleChange}
                identical={compare("FirstEntry")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.FirstEntry["@value"] === "1" && (
                <h6 className="text-center">
                  <i>
                    If yes, the foster parent will have the child medically
                    screened within 24 hours of placement.
                  </i>
                </h6>
              )}
              <RadioButton
                name="HealthConcerns"
                label="Are there any health (bx or physical) concerns that require immediate attention?"
                options={yesNoOptions}
                error={errors.HealthConcerns}
                value={data.HealthConcerns["@value"]}
                onChange={handleChange}
                identical={compare("HealthConcerns")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.HealthConcerns["@value"] === "1" && (
                <TextArea
                  name="HealthConcernsDetails"
                  label="If yes, please describe:"
                  error={errors.HealthConcernsDetails}
                  value={data.HealthConcernsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("HealthConcernsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="Allergies"
                label="Allergies &amp; Reaction"
                options={yesNoOptions}
                error={errors.Allergies}
                value={data.Allergies["@value"]}
                onChange={handleChange}
                identical={compare("Allergies")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Allergies["@value"] === "1" && (
                <RadioButton
                  name="EpiPen"
                  label="Epi-pen?"
                  options={yesNoOptions}
                  error={errors.EpiPen}
                  value={data.EpiPen["@value"]}
                  onChange={handleChange}
                  identical={compare("EpiPen")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="RecentHospitalizations"
                label="Recent hospitalizations or surgeries?"
                options={yesNoOptions}
                error={errors.RecentHospitalizations}
                value={data.RecentHospitalizations["@value"]}
                onChange={handleChange}
                identical={compare("RecentHospitalizations")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.RecentHospitalizations["@value"] === "1" && (
                <TextArea
                  name="RecentHospitalizationsDetails"
                  label="If yes, describe detail including complications or follow-up:"
                  error={errors.RecentHospitalizationsDetails}
                  value={data.RecentHospitalizationsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("RecentHospitalizationsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="Height"
                      label="Height"
                      error={errors.Height}
                      value={data.Height["@value"]}
                      onChange={handleChange}
                      identical={compare("Height")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Weight"
                      label="Weight"
                      error={errors.Weight}
                      value={data.Weight["@value"]}
                      onChange={handleChange}
                      identical={compare("Weight")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="HairColor"
                      label="Hair Color"
                      error={errors.HairColor}
                      value={data.HairColor["@value"]}
                      onChange={handleChange}
                      identical={compare("HairColor")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="EyeColor"
                      label="Eye Color"
                      error={errors.EyeColor}
                      value={data.EyeColor["@value"]}
                      onChange={handleChange}
                      identical={compare("EyeColor")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <Input
                name="LastDoctorVisit"
                label="Date of last doctor visit:"
                error={errors.LastDoctorVisit}
                value={data.LastDoctorVisit["@value"]}
                onChange={handleChange}
                identical={compare("LastDoctorVisit")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="LastDoctorVisitIssues"
                label="Issues/follow up:"
                error={errors.LastDoctorVisitIssues}
                value={data.LastDoctorVisitIssues["@value"]}
                onChange={handleChange}
                identical={compare("LastDoctorVisitIssues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="LastDentalVisit"
                label="Date of last dental visit:"
                error={errors.LastDentalVisit}
                value={data.LastDentalVisit["@value"]}
                onChange={handleChange}
                identical={compare("LastDentalVisit")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="LastDentalVisitIssues"
                label="Issues/follow up:"
                error={errors.LastDentalVisitIssues}
                value={data.LastDentalVisitIssues["@value"]}
                onChange={handleChange}
                identical={compare("LastDentalVisitIssues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="ImmunizationRecords"
                label="Are youth’s immunization records Included?"
                options={yesNoOptions}
                error={errors.ImmunizationRecords}
                value={data.ImmunizationRecords["@value"]}
                onChange={handleChange}
                identical={compare("ImmunizationRecords")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ImmunizationRecords["@value"] === "0" && (
                <TextArea
                  name="ImmunizationRecordsPlan"
                  label="If no, please identify plan to obtain them within two weeks of placement:"
                  error={errors.ImmunizationRecordsPlan}
                  value={data.ImmunizationRecordsPlan["@value"]}
                  onChange={handleChange}
                  identical={compare("ImmunizationRecordsPlan")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <h6 className="text-center mt-4">Safety and Support</h6>
              <RadioButton
                name="RecentSelfHarm"
                label="Has the youth expressed recent SI or HI, or other self-harming behavior?"
                options={yesNoOptions}
                error={errors.RecentSelfHarm}
                value={data.RecentSelfHarm["@value"]}
                onChange={handleChange}
                identical={compare("RecentSelfHarm")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.RecentSelfHarm["@value"] === "1" && (
                <>
                  <TextArea
                    name="RecentSelfHarmDetails"
                    label="If yes, please detail:"
                    error={errors.RecentSelfHarmDetails}
                    value={data.RecentSelfHarmDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("RecentSelfHarmDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <h6>
                    <i>
                      SAFY will complete a C-SSRS Screener at Placement and will
                      complete a Safety Plan if needed.
                    </i>
                  </h6>
                </>
              )}
              <TextArea
                name="YouthTriggers"
                label="Youth’s Triggers"
                error={errors.YouthTriggers}
                value={data.YouthTriggers["@value"]}
                onChange={handleChange}
                identical={compare("YouthTriggers")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="EffectiveStrategies"
                label="Effective Strategies"
                error={errors.EffectiveStrategies}
                value={data.EffectiveStrategies["@value"]}
                onChange={handleChange}
                identical={compare("EffectiveStrategies")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="SupervisionNeeds"
                label="Special Supervision Needs"
                error={errors.SupervisionNeeds}
                value={data.SupervisionNeeds["@value"]}
                onChange={handleChange}
                identical={compare("SupervisionNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="Unsupervised"
                label="May the youth be left unsupervised?"
                options={yesNoOptions}
                error={errors.Unsupervised}
                value={data.Unsupervised["@value"]}
                onChange={handleChange}
                identical={compare("Unsupervised")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Unsupervised["@value"] === "1" && (
                <TextArea
                  name="UnsupervisedDetails"
                  label="If yes, describe amount of time and situations:"
                  error={errors.UnsupervisedDetails}
                  value={data.UnsupervisedDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("UnsupervisedDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <h6 className="text-center mt-4">Family Contact</h6>
              <RadioButton
                name="ContactFamily"
                label="Can SAFY have contact with the youth’s primary family?"
                options={yesNoOptions}
                error={errors.ContactFamily}
                value={data.ContactFamily["@value"]}
                onChange={handleChange}
                identical={compare("ContactFamily")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ContactFamily["@value"] === "1" && (
                <TextArea
                  name="ContactFamilyNames"
                  label="If yes, list the names of approved individuals:"
                  error={errors.ContactFamilyNames}
                  value={data.ContactFamilyNames["@value"]}
                  onChange={handleChange}
                  identical={compare("ContactFamilyNames")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="ContactRestrictions"
                label="Are there any restrictions to the contact?"
                options={yesNoOptions}
                error={errors.ContactRestrictions}
                value={data.ContactRestrictions["@value"]}
                onChange={handleChange}
                identical={compare("ContactRestrictions")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ContactRestrictions["@value"] === "1" && (
                <TextArea
                  name="ContactRestrictionsDetails"
                  label="If yes, please describe:"
                  error={errors.ContactRestrictionsDetails}
                  value={data.ContactRestrictionsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ContactRestrictionsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <h6 className="text-center mt-4">Visitation</h6>
              <RadioButton
                name="FamilyVisitation"
                label="Will the youth have visitation with family?"
                options={yesNoOptions}
                error={errors.FamilyVisitation}
                value={data.FamilyVisitation["@value"]}
                onChange={handleChange}
                identical={compare("FamilyVisitation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.FamilyVisitation["@value"] === "1" && (
                <>
                  <Input
                    name="VisitationFrequency"
                    label="Frequency:"
                    error={errors.VisitationFrequency}
                    value={data.VisitationFrequency["@value"]}
                    onChange={handleChange}
                    identical={compare("VisitationFrequency")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="VisitationLocation"
                    label="Location:"
                    error={errors.VisitationLocation}
                    value={data.VisitationLocation["@value"]}
                    onChange={handleChange}
                    identical={compare("VisitationLocation")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    name="VisitationDuration"
                    label="Duration:"
                    error={errors.VisitationDuration}
                    value={data.VisitationDuration["@value"]}
                    onChange={handleChange}
                    identical={compare("VisitationDuration")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <TextArea
                    name="VisitationRestrictions"
                    label="Restrictions (if any):"
                    error={errors.VisitationRestrictions}
                    value={data.VisitationRestrictions["@value"]}
                    onChange={handleChange}
                    identical={compare("VisitationRestrictions")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <RadioButton
                name="NoContact"
                label="Is there anybody the youth CANNOT have contact with?"
                options={yesNoOptions}
                error={errors.NoContact}
                value={data.NoContact["@value"]}
                onChange={handleChange}
                identical={compare("NoContact")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.NoContact["@value"] === "1" && (
                <TextArea
                  name="NoContactDetails"
                  label="If so, please indicate:"
                  error={errors.NoContactDetails}
                  value={data.NoContactDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("NoContactDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <h6 className="text-center mt-4">
                Other Placement Considerations or Notes (including Special
                Needs)
              </h6>
              <TextArea
                name="OtherNotes"
                error={errors.OtherNotes}
                value={data.OtherNotes["@value"]}
                onChange={handleChange}
                identical={compare("TextAre")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div>
                My signature indicates the client’s needs have been reviewed
                with me and I agree to adhere to the action items above.
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default COTFCPlacementSheet;
